import { Rng } from '../core/rng';

/**
 * PROCEDURAL MUSIC
 *
 * No audio assets exist (offline sandbox — see docs/DECISIONS.md), so the
 * "soundtrack" is generated live with Web Audio oscillators/noise, the same
 * way SynthAudio's SFX blips are. Each era gets a small "preset" (scale,
 * tempo, root note, a short chord progression and an instrument/waveform
 * mix) that is looked up by `era.index`; the arpeggio/melody choices inside
 * a preset are seeded from `Rng` with the era index so a given era always
 * sounds the same, but different eras sound different from each other.
 *
 * Layers (pad / bass / arp / perc, plus two boss-only extras) are separate
 * mix busses (GainNodes) that are always being fed notes once the scheduler
 * is running; a track *state* (menu/hub/stage/boss/none) just decides which
 * busses are audible, and switching state linearly ramps each bus's gain
 * over ~1.5s so there is never a hard cut — the notes themselves are
 * unaffected, so ramping can never truncate an in-flight envelope abruptly.
 *
 * Scheduling follows the standard "lookahead" Web-Audio pattern: a
 * `setInterval` fires every 25ms and, each time, schedules every note whose
 * start time falls within the next 100ms, using `AudioParam`/`start(when)`
 * sample-accurate timestamps rather than firing sounds "now" — this avoids
 * the jitter you'd get from scheduling directly off the `setInterval` tick.
 */

export type MusicState = 'menu' | 'hub' | 'stage' | 'boss' | 'none';

type Layer = 'pad' | 'bass' | 'arp' | 'perc' | 'bassDrive' | 'percBoss';

/* ------------------------------------------------------------------------ */
/* Scales & presets                                                          */
/* ------------------------------------------------------------------------ */

// Semitone offsets from the root, one octave. Picked for mood, not rigor.
const SCALES = {
  minorPent: [0, 3, 5, 7, 10],
  majorPent: [0, 2, 4, 7, 9],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  naturalMinor: [0, 2, 3, 5, 7, 8, 10],
  harmonicMinor: [0, 2, 3, 5, 7, 8, 11],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  locrian: [0, 1, 3, 5, 6, 8, 10],
  wholeTone: [0, 2, 4, 6, 8, 10],
} as const;

interface EraPreset {
  scale: readonly number[];
  root: number; // MIDI note number
  tempo: number; // BPM (quarter note), before boss +15%
  /** Root scale-degree per bar (chord = degree, degree+2, degree+4 within the scale, wrapping). */
  barRootDegrees: readonly number[];
  padWave: OscillatorType;
  bassWave: OscillatorType;
  arpWave: OscillatorType;
}

// Every implemented era has a distinct deterministic preset. These remain
// intentionally compact until a future real-audio pass replaces or layers
// over the procedural score.
const PRESETS: readonly EraPreset[] = [
  // 0 — Primordial Soup: slow, watery, minor pentatonic pads.
  { scale: SCALES.minorPent, root: 45, tempo: 68, barRootDegrees: [0, 0, 3, 2], padWave: 'sine', bassWave: 'triangle', arpWave: 'triangle' },
  // 1 — Cambrian Seas: dorian, plucky arps + soft bass.
  { scale: SCALES.dorian, root: 48, tempo: 104, barRootDegrees: [0, 3, 4, 0], padWave: 'triangle', bassWave: 'sine', arpWave: 'triangle' },
  // 2 — Age of Dinosaurs: mixolydian, low sawtooth drone + percussion-forward.
  { scale: SCALES.mixolydian, root: 40, tempo: 92, barRootDegrees: [0, 0, 5, 4], padWave: 'sawtooth', bassWave: 'triangle', arpWave: 'square' },
  // 3 — Ice Age: harmonic minor, sparse and cold.
  { scale: SCALES.harmonicMinor, root: 43, tempo: 80, barRootDegrees: [0, 4, 3, 0], padWave: 'sine', bassWave: 'sine', arpWave: 'triangle' },
  // 4 — Stone Age: phrygian, driving.
  { scale: SCALES.phrygian, root: 41, tempo: 112, barRootDegrees: [0, 1, 0, 4], padWave: 'triangle', bassWave: 'square', arpWave: 'square' },
  // 5 — Ancient Empires: lydian, bright/open.
  { scale: SCALES.lydian, root: 50, tempo: 96, barRootDegrees: [0, 3, 4, 3], padWave: 'triangle', bassWave: 'triangle', arpWave: 'sine' },
  // 6 — Medieval: locrian, tense.
  { scale: SCALES.locrian, root: 38, tempo: 100, barRootDegrees: [0, 2, 4, 1], padWave: 'sawtooth', bassWave: 'sine', arpWave: 'triangle' },
  // 7 — Industrial: whole tone, mechanical/unsettled.
  { scale: SCALES.wholeTone, root: 52, tempo: 84, barRootDegrees: [0, 2, 4, 0], padWave: 'sawtooth', bassWave: 'square', arpWave: 'square' },
  // 8 — Near Future: major pentatonic, upbeat/synthetic.
  { scale: SCALES.majorPent, root: 47, tempo: 120, barRootDegrees: [0, 4, 3, 4], padWave: 'sine', bassWave: 'triangle', arpWave: 'sine' },
  // 9 — End of Time: natural minor, slow and vast.
  { scale: SCALES.naturalMinor, root: 36, tempo: 90, barRootDegrees: [0, 3, 4, 0], padWave: 'triangle', bassWave: 'sine', arpWave: 'triangle' },
];

// Boss fights are always in a natural-minor key over the era's root, for a
// consistent "danger" flavor regardless of which era's boss it is.
const BOSS_SCALE = SCALES.naturalMinor;

/* ------------------------------------------------------------------------ */
/* Mix targets per track state                                               */
/* ------------------------------------------------------------------------ */

// Gains are kept low (≤0.25) since several layers can be active/crossfading
// at once and this all sits behind the user's separate music volume slider.
// Menu/hub sit well under gameplay: the pad plays alone there, so nothing
// masks it (reported 2026-09-25 as too loud and bassy).
const STATE_TARGETS: Record<MusicState, Partial<Record<Layer, number>>> = {
  none: {},
  menu: { pad: 0.12 },
  hub: { pad: 0.11, arp: 0.09 },
  stage: { pad: 0.14, bass: 0.18, arp: 0.15, perc: 0.13 },
  boss: { pad: 0.11, bass: 0.2, arp: 0.16, perc: 0.15, bassDrive: 0.14, percBoss: 0.12 },
};

const RAMP_SEC = 1.5;
const STEPS_PER_BAR = 16; // sixteenth-note grid

function midiToFreq(m: number): number {
  return 440 * Math.pow(2, (m - 69) / 12);
}

/** Lowest pad note (A2, 110 Hz): era roots dropped an octave otherwise land at 33–82 Hz and boom. */
export const PAD_FLOOR_MIDI = 45;
/** Lowest bass note (A1, 55 Hz): two octaves under low era roots is inaudible sub that only eats headroom. */
export const BASS_FLOOR_MIDI = 33;

/** Raises a MIDI note by whole octaves until it is at or above `floor`. */
export function atOrAbove(midi: number, floor: number): number {
  let m = midi;
  while (m < floor) m += 12;
  return m;
}

/** Maps a (possibly out-of-octave) scale degree to a MIDI note relative to root. */
function degreeToMidi(scale: readonly number[], root: number, degree: number): number {
  const len = scale.length;
  const wrapped = ((degree % len) + len) % len;
  const octave = Math.floor(degree / len);
  return root + scale[wrapped] + 12 * octave;
}

export class ProceduralMusic {
  private readonly ctx: AudioContext;
  private readonly out: GainNode;
  private readonly layerGains: Record<Layer, GainNode>;
  private readonly padFilter: BiquadFilterNode;
  private readonly bassFilter: BiquadFilterNode;
  private readonly noiseBuffer: AudioBuffer;

  private preset: EraPreset = PRESETS[0];
  private eraIndex = 0;
  private arpPattern: (number | null)[] = [];

  private state: MusicState = 'none';
  private targetState: MusicState = 'none';
  private activeLayers = new Set<Layer>();
  private transitionTimer: ReturnType<typeof setTimeout> | null = null;

  private timer: ReturnType<typeof setInterval> | null = null;
  private nextNoteTime = 0;
  private step = 0;

  constructor(ctx: AudioContext, destination: AudioNode) {
    this.ctx = ctx;
    this.out = ctx.createGain();
    this.out.gain.value = 1;
    // Strip inaudible sub-bass from the whole music bus so it can't eat headroom.
    const subCut = ctx.createBiquadFilter();
    subCut.type = 'highpass';
    subCut.frequency.value = 30;
    subCut.Q.value = 0.5;
    this.out.connect(subCut).connect(destination);

    // Persistent lowpass on the pad bus, slowly swept by an LFO — gives the
    // "watery"/breathing quality asked for without rebuilding a filter per
    // note. Keeps the sawtooth-based drone presets (era 2, etc.) from being
    // harsh (lowpass on saws).
    this.padFilter = ctx.createBiquadFilter();
    this.padFilter.type = 'lowpass';
    this.padFilter.frequency.value = 700;
    this.padFilter.Q.value = 0.6;
    const padLFO = ctx.createOscillator();
    padLFO.frequency.value = 0.07; // slow — a full sweep every ~14s
    const padLFOGain = ctx.createGain();
    padLFOGain.gain.value = 260;
    padLFO.connect(padLFOGain).connect(this.padFilter.frequency);
    padLFO.start();

    this.bassFilter = ctx.createBiquadFilter();
    this.bassFilter.type = 'lowpass';
    this.bassFilter.frequency.value = 900;
    this.bassFilter.Q.value = 0.3;

    this.layerGains = {
      pad: this.makeLayerGain(),
      bass: this.makeLayerGain(),
      arp: this.makeLayerGain(),
      perc: this.makeLayerGain(),
      bassDrive: this.makeLayerGain(),
      percBoss: this.makeLayerGain(),
    };
    // Gentle low cut after the pad's lowpass: keeps its warmth out of the kick/bass range.
    const padLowCut = ctx.createBiquadFilter();
    padLowCut.type = 'highpass';
    padLowCut.frequency.value = 90;
    padLowCut.Q.value = 0.5;
    this.padFilter.connect(padLowCut).connect(this.layerGains.pad);
    this.bassFilter.connect(this.layerGains.bass);

    this.noiseBuffer = this.makeNoiseBuffer();
    this.setEra(0);
  }

  private makeLayerGain(): GainNode {
    const g = this.ctx.createGain();
    g.gain.value = 0;
    g.connect(this.out);
    return g;
  }

  /** One second of white noise, centered at 0 (no DC offset), reused for every burst. */
  private makeNoiseBuffer(): AudioBuffer {
    const len = Math.max(1, Math.floor(this.ctx.sampleRate * 1));
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    return buf;
  }

  /** Master music output gain (0..1), independent of SFX. */
  setOutputGain(v: number): void {
    this.out.gain.value = v;
  }

  /** Switches the era-specific instrumentation/scale/tempo. Same seed → same tune every time. */
  setEra(index: number): void {
    this.eraIndex = index;
    this.preset = PRESETS[index] ?? PRESETS[PRESETS.length - 1];
    const rng = new Rng(`era-music-${index}`);
    this.arpPattern = [];
    for (let i = 0; i < STEPS_PER_BAR; i++) {
      this.arpPattern.push(rng.chance(0.15) ? null : rng.int(0, 3));
    }
  }

  /** Crossfades to a new track state over ~1.5s; never clicks. */
  setState(state: MusicState): void {
    if (state === this.targetState) return;
    const prevState = this.targetState;
    this.targetState = state;
    const now = this.ctx.currentTime;

    const layers = new Set<Layer>([
      ...(Object.keys(STATE_TARGETS[prevState]) as Layer[]),
      ...(Object.keys(STATE_TARGETS[state]) as Layer[]),
    ]);
    for (const layer of layers) {
      const g = this.layerGains[layer].gain;
      const target = STATE_TARGETS[state][layer] ?? 0;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(target, now + RAMP_SEC);
    }
    this.activeLayers = layers;

    if (this.transitionTimer !== null) clearTimeout(this.transitionTimer);
    this.transitionTimer = setTimeout(() => {
      this.state = state;
      this.activeLayers = new Set(Object.keys(STATE_TARGETS[state]) as Layer[]);
      if (state === 'none') this.stopScheduler();
    }, RAMP_SEC * 1000 + 60);

    if (state !== 'none') this.startScheduler();
  }

  private startScheduler(): void {
    if (this.timer !== null) return;
    this.step = 0;
    this.nextNoteTime = this.ctx.currentTime + 0.05;
    this.timer = setInterval(() => this.scheduleAhead(), 25);
  }

  private stopScheduler(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private stepDurationSec(): number {
    const boss = this.targetState === 'boss';
    const bpm = this.preset.tempo * (boss ? 1.15 : 1);
    return 60 / bpm / 4; // sixteenth note
  }

  private scheduleAhead(): void {
    const lookahead = 0.1;
    while (this.nextNoteTime < this.ctx.currentTime + lookahead) {
      this.scheduleStep(this.nextNoteTime, this.step);
      this.nextNoteTime += this.stepDurationSec();
      this.step = (this.step + 1) % (STEPS_PER_BAR * this.preset.barRootDegrees.length);
    }
  }

  private currentChordDegrees(bar: number): { scale: readonly number[]; degrees: [number, number, number] } {
    const boss = this.targetState === 'boss';
    const scale = boss ? BOSS_SCALE : this.preset.scale;
    const rootDeg = this.preset.barRootDegrees[bar % this.preset.barRootDegrees.length];
    return { scale, degrees: [rootDeg, rootDeg + 2, rootDeg + 4] };
  }

  private scheduleStep(t: number, step: number): void {
    const posInBar = step % STEPS_PER_BAR;
    const bar = Math.floor(step / STEPS_PER_BAR);
    const { scale, degrees } = this.currentChordDegrees(bar);
    const barDur = STEPS_PER_BAR * this.stepDurationSec();

    if (posInBar === 0 && this.activeLayers.has('pad')) this.playPad(t, scale, degrees, barDur);
    if ((posInBar === 0 || posInBar === 8) && this.activeLayers.has('bass')) this.playBass(t, scale, degrees[0]);
    if (this.activeLayers.has('bassDrive') && posInBar % 4 === 2) this.playBassDrive(t, scale, degrees[0]);
    if (this.activeLayers.has('arp') && posInBar % 2 === 0) this.playArp(t, step, scale, degrees);
    if (this.activeLayers.has('perc')) this.playPerc(t, posInBar);
    if (this.activeLayers.has('percBoss')) this.playPercBoss(t, posInBar);
  }

  /* ---------------------------------------------------------------- notes */

  private playPad(t: number, scale: readonly number[], degrees: [number, number, number], dur: number): void {
    const attack = 0.5;
    const release = 0.6;
    for (const deg of degrees) {
      // An octave down so it sits under the arp, but never into sub-bass.
      const freq = midiToFreq(atOrAbove(degreeToMidi(scale, this.preset.root, deg - 12), PAD_FLOOR_MIDI));
      const osc = this.ctx.createOscillator();
      osc.type = this.preset.padWave;
      osc.frequency.value = freq;
      // Three chord tones stack on the same bus, so keep each one's peak
      // well under unity to avoid the bus summing too hot.
      const peak = 0.5;
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(peak, t + attack);
      g.gain.setValueAtTime(peak, Math.max(t + attack, t + dur - release));
      g.gain.linearRampToValueAtTime(0.0001, t + dur);
      osc.connect(g).connect(this.padFilter);
      osc.start(t);
      osc.stop(t + dur + 0.05);
    }
  }

  private playBass(t: number, scale: readonly number[], rootDeg: number): void {
    const freq = midiToFreq(atOrAbove(degreeToMidi(scale, this.preset.root, rootDeg - 24), BASS_FLOOR_MIDI));
    const dur = this.stepDurationSec() * 7; // roughly a half note, leaves a gap before the next hit
    const osc = this.ctx.createOscillator();
    osc.type = this.preset.bassWave;
    osc.frequency.value = freq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(1, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g).connect(this.bassFilter);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  /** Boss-only: a driving eighth-note pulse under the regular bass, for extra urgency. */
  private playBassDrive(t: number, scale: readonly number[], rootDeg: number): void {
    const freq = midiToFreq(degreeToMidi(scale, this.preset.root, rootDeg - 12));
    const dur = this.stepDurationSec() * 1.6;
    const osc = this.ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = freq;
    const filt = this.ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = 500;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(1, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(filt).connect(g).connect(this.layerGains.bassDrive);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  private playArp(t: number, step: number, scale: readonly number[], degrees: [number, number, number]): void {
    const pick = this.arpPattern[step % this.arpPattern.length];
    if (pick === null) return;
    const deg = degrees[pick % degrees.length];
    const freq = midiToFreq(degreeToMidi(scale, this.preset.root, deg));
    const dur = this.stepDurationSec() * 1.4;
    const osc = this.ctx.createOscillator();
    osc.type = this.preset.arpWave;
    osc.frequency.value = freq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(1, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g).connect(this.layerGains.arp);
    osc.start(t);
    osc.stop(t + dur + 0.03);
  }

  private noiseBurst(t: number, dur: number, dest: AudioNode, filterType: BiquadFilterType, filterFreq: number, peak: number): void {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    const filt = this.ctx.createBiquadFilter();
    filt.type = filterType;
    filt.frequency.value = filterFreq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filt).connect(g).connect(dest);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  /** Kick (low, on the downbeats) + hat (short, high) via filtered noise bursts. */
  private playPerc(t: number, posInBar: number): void {
    if (posInBar % 8 === 0) this.noiseBurst(t, 0.14, this.layerGains.perc, 'lowpass', 140, 1);
    else if (posInBar % 4 === 2) this.noiseBurst(t, 0.04, this.layerGains.perc, 'highpass', 6000, 0.6);
  }

  /** Boss-only: extra syncopated hits layered on top of the regular kit. */
  private playPercBoss(t: number, posInBar: number): void {
    if (posInBar % 8 === 6) this.noiseBurst(t, 0.1, this.layerGains.percBoss, 'bandpass', 900, 0.8);
    else if (posInBar % 2 === 1) this.noiseBurst(t, 0.03, this.layerGains.percBoss, 'highpass', 8000, 0.4);
  }
}
